import axios from 'axios';
import flatstore from 'flatstore';
import {siteInfo, defaultLang,urlMaps} from './config';
import keys from 'lodash/keys';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import trim from 'lodash/trim';
import isNull from 'lodash/isNull';
import isNil from 'lodash/isNil';
import first from 'lodash/first';
import {getStorage, setStorage} from './storage';
import i18n from '../init/i18n';

flatstore.set('loading', false);

/** Coalesce concurrent GETs to the same URL (multiple flatstore keys share one response). */
const inflightGetByUrl = {};

function trackGetWaiter(url, datastate) {
    if (!inflightGetByUrl[url]) {
        inflightGetByUrl[url] = [];
    }
    if (datastate && !inflightGetByUrl[url].includes(datastate)) {
        inflightGetByUrl[url].push(datastate);
    }
    return inflightGetByUrl[url].length > 1;
}

function resolveGetWaiters(url, data) {
    const keys = inflightGetByUrl[url] || [];
    delete inflightGetByUrl[url];
    keys.forEach((key) => {
        if (key) {
            flatstore.set(key, data);
        }
    });
}

function rejectGetWaiters(url) {
    delete inflightGetByUrl[url];
}

if (!String.prototype.replaceAll) {
    String.prototype.replaceAll = function(str, newStr){
        // If a regex pattern
        if (Object.prototype.toString.call(str).toLowerCase() === '[object regexp]') {
            return this.replace(str, newStr);
        }
        // If a string
        return this.replace(new RegExp(str, 'g'), newStr);
    };
}

export function get(url, datastate, options={}){
    if (trackGetWaiter(url, datastate)) {
        return;
    }

    flatstore.set('err_data', null); // clear any prior request's error so it can't leak into this page (e.g. false 404s)
    flatstore.set('loading', true);
    try{
        axios.get(url, options).then(function(response){
            resolveGetWaiters(url, response.data);
            flatstore.set('loading', false);

        }).catch(err => {
            rejectGetWaiters(url);
            if(err.response){
                flatstore.set('err_data', err.response.status);
                flatstore.set('loading', false);
            }

        });
    }catch(err){
        rejectGetWaiters(url);
        if(err.response){
            flatstore.set('err_data', err.response.status);
            flatstore.set('loading', false);
        }
    }
}

export function getR(url, datastate, options={}){
    if (trackGetWaiter(url, datastate)) {
        return;
    }

    flatstore.set('err_data', null);
    try{
        axios.get(url, options).then(function(response){
            resolveGetWaiters(url, response.data);
        }).catch(err => {
            rejectGetWaiters(url);
            if(err.response){
                flatstore.set('err_data', err.response.status);
                flatstore.set('loading', false);
            }

        });
    }catch(err){
        rejectGetWaiters(url);
        if(err.response){
            flatstore.set('err_data', err.response.status);
            flatstore.set('loading', false);
        }
    }
}

export async function getSync(url, datastate, options={}){
    flatstore.set('err_data', null);
    flatstore.set('loading', true);

    try{
        await axios.get(url, options).then(function(response){
            flatstore.set(datastate, response.data);
            flatstore.set('loading', false);
        });
    }catch(err){
        if(err.response){
            flatstore.set('err_data', err.response.status);
        }
        flatstore.set('loading', false);
    }
}

// using it for modules like relatedProducts
export function getWithoutError(url, datastate, options={}){
    try{
        axios.get(url, options).then(function(response){
            flatstore.set(datastate, response.data);
        }).catch(err => {

        });
    }catch(err){

    }
}

export function post(url, postdata, datastate, options={}){
    flatstore.set('loading', true);
    try{
        axios.post(url, postdata, options).then(function(response){flatstore.set(datastate, response.data);flatstore.set('loading', false);});
    }catch(err){
        console.log(err);
    }
}

export async function postSync(url, postdata, datastate, options={}){
    flatstore.set('loading', true);
    try{
        await axios.post(url, postdata, options).then(function(response){flatstore.set(datastate, response.data); flatstore.set('loading', false);});
    }catch(err){
        console.log(err);
    }
}

export function postR(url, postdata, datastate, options={}){
    try{
        axios.post(url, postdata, options).then(function(response){flatstore.set(datastate, response.data);});
    }catch(err){
        console.log(err);
    }
}

export function isMobile(){
    if(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)){
            return true
    }
    else{
            return false
    }
}

export function currentLang() {
    var lang = window.location.pathname.substring(1,3);
    if(includes(keys(siteInfo), lang)){
        return lang;
    }else{
        return defaultLang;
    }
}

export function apiURL() {
    try {
        // Safely check if window and localStorage are available
        const isBrowser = typeof window !== 'undefined';
        const href = isBrowser ? window.location.href : '';

        let baseURL = process.env.REACT_APP_URL;

        // Validate if the environment variables are defined
        if (!baseURL) {
            throw new Error('REACT_APP_URL is not defined');
        }

        if (isBrowser) {
            let isDev = false;
            try {
                // Safely access localStorage which might be blocked in in-app browsers
                isDev = localStorage.getItem('bypass_api_cache') === 'true';
            } catch (e) {
                // localStorage not available, continue with default
                console.warn('localStorage access failed in apiURL');
            }

            if (isDev) {
                baseURL = process.env.REACT_APP_DEV_URL || baseURL; // Fallback to REACT_APP_URL if DEV_URL is not defined
            }

            if (href.includes('local')) {
                baseURL = process.env.REACT_APP_LOCAL_URL || baseURL; // Fallback to REACT_APP_URL if LOCAL_URL is not defined
            } else if (href.includes('alpha') || href.includes('staging')) {
                baseURL = process.env.REACT_APP_STAGING_URL || baseURL; // Fallback to REACT_APP_URL if STAGING_URL is not defined
            }
        }

        if (!currentLang() || !siteInfo[currentLang()]) {
            throw new Error('Current language or site info is not available');
        }

        const baseParams = `/${currentLang()}/${siteInfo[currentLang()]['lang']}`;

        return `${baseURL}${baseParams}`;
    } catch (error) {
        console.error('Error generating API URL:', error);
        return ''; // Return an empty string or handle the error as per the requirement
    }
}

export function isProductType(productType = 'products') {
    const currentLanguage = currentLang();

    // Helper function to sanitize URL pattern
    function sanitizePattern(pattern) {
        return pattern.replace(':slug?', '').replace(currentLanguage, '').replaceAll('/', '');
    }

    const rowMatchesType = (rowComponent, type) => {
        if (type === 'subscription') {
            return rowComponent === 'subscription' || rowComponent === 'static-subscription';
        }
        return rowComponent === type;
    };

    let productPatterns = [];
    urlMaps[currentLanguage].forEach(item => {
        const topMatches =
            item.name === productType ||
            (productType === 'subscription' && item.component === 'static-subscription');
        if (topMatches) {
            productPatterns.push(sanitizePattern(item.pattern));
        }

        if (item.items) {
            item.items.forEach(row => {
                const tempURL = row.pattern.replace('/' + currentLanguage, '');
                if (!isEmpty(row.pattern) && !isEmpty(tempURL) && rowMatchesType(row.component, productType)) {
                    productPatterns.push(sanitizePattern(row.pattern));
                }
            });
        }
    });

    if (productType === 'products') {
        window.product_urls = productPatterns;
    } else if (productType === 'farmshop-products') {
        window.farmshop_product_urls = productPatterns;
    }

    const url = window.location.pathname.split('/').filter(str => str.length > 1);
    return !!(url && url[1] && productPatterns.includes(url[1]));
}

/** @returns {object|null} urlMaps entry for full-page subscription + step routes */
export const getSubscriptionFlowConfig = (pathname) => {
    const normalized = (pathname || window.location.pathname).replace(/\/$/, '') || '/';
    const currentLanguage = currentLang();
    const maps = urlMaps[currentLanguage] || [];
    return maps.find(
        (item) =>
            item.component === 'subscription' &&
            (normalized === item.pattern || normalized.startsWith(item.pattern + '/'))
    ) || null;
};

export const getSubscriptionEmbedSlug = (pathname) =>
    getSubscriptionFlowConfig(pathname)?.embedStaticSlug ?? 'subscription';

/** @returns {object|null} urlMaps entry for static page + embedded subscription wizard */
export const getStaticSubscriptionFlowConfig = (pathname) => {
    const normalized = (pathname || window.location.pathname).replace(/\/$/, '') || '/';
    const currentLanguage = currentLang();
    const maps = urlMaps[currentLanguage] || [];
    return maps.find(
        (item) =>
            item.component === 'static-subscription' &&
            (normalized === item.pattern || normalized.startsWith(item.pattern + '/'))
    ) || null;
};

export const getStaticSubscriptionEmbedSlug = (pathname) =>
    getStaticSubscriptionFlowConfig(pathname)?.embedStaticSlug ?? null;

/** Reject unknown path segments under a static-subscription base (e.g. …/typo). */
export const staticSubscriptionPathIsValid = (pathname) => {
    const cfg = getStaticSubscriptionFlowConfig(pathname);
    if (!cfg) {
        return false;
    }
    const normalized = pathname.replace(/\/$/, '') || '/';
    if (normalized === cfg.pattern) {
        return true;
    }
    if (!normalized.startsWith(cfg.pattern + '/')) {
        return false;
    }
    const remainder = normalized.slice(cfg.pattern.length + 1);
    if (remainder.includes('/')) {
        return false;
    }
    const stepSuffixes = (cfg.items || []).map((it) => {
        const p = (it.pattern || '').replace(/\/$/, '');
        return p.split('/').pop();
    });
    return stepSuffixes.includes(remainder);
};

export const getSubscriptionPaths = () => {
    const currentLanguage = currentLang();
    const pathname = window.location.pathname.replace(/\/$/, '') || '/';
    const flowConfigs = urlMaps[currentLanguage].filter(
        (item) => item.component === 'subscription' || item.component === 'static-subscription'
    );
    const matched = flowConfigs.find(
        (item) => pathname === item.pattern || pathname.startsWith(item.pattern + '/')
    );
    const subscriptionConfig =
        matched ||
        flowConfigs.find((item) => item.component === 'subscription') ||
        flowConfigs[0];
    let paths = {
        base: subscriptionConfig?.pattern,
        steps: {}
    };
    if (subscriptionConfig && subscriptionConfig.items) {
        subscriptionConfig.items.forEach(item => {
            paths.steps[item.name] = item.pattern;
        });
    }
    return paths;
};

export const localStorageUtil = {
  // Method to set any type of data in localStorage
  setItem(key, value) {
    try {
      let valueToStore;
      if (typeof value === 'object' && value !== null) {
        valueToStore = JSON.stringify(value);
      } else {
        valueToStore = value;
      }
      localStorage.setItem(key, valueToStore);
    } catch (e) {
      console.warn('localStorage.setItem failed:', e);
      // Silently fail if localStorage is not available (e.g., in private mode or in-app browsers)
    }
  },

  // Method to get any type of data from localStorage
  getItem(key) {
    try {
      const value = localStorage.getItem(key);
      try {
        return JSON.parse(value);
      } catch (e) {
        return value;
      }
    } catch (e) {
      console.warn('localStorage.getItem failed:', e);
      return null;
    }
  },

  // Method to update an object in localStorage
  updateObjectItem(key, partialObject) {
    try {
      // Get the current object, or initialize to an empty object if not present
      const currentItem = this.getItem(key) || {};
      // Merge the new data with the existing object
      const updatedItem = { ...currentItem, ...partialObject };
      // Save the updated object back to localStorage
      this.setItem(key, updatedItem);
    } catch (e) {
      console.warn('localStorage.updateObjectItem failed:', e);
    }
  },

  // Method to remove an item from localStorage
  removeItem(key) {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.warn('localStorage.removeItem failed:', e);
    }
  }
};

export function productURL(url, slug, type){
    var arr = [];
    var defaultURL = '';
    urlMaps[currentLang()].map(item=>{
        if(item.name == 'products')
            defaultURL = item.pattern.replace(':slug?', '');
        if(item['items']){
          item['items'].map(row=>{
            var tempURL = row.pattern.replace('/'+currentLang(), '');
            if(!isEmpty(row.pattern) && !isEmpty(tempURL)){
              if(row.component == 'products'){
                arr[row.name] = row;
              }
            }
          })
        }
      })
    if(includes(keys(arr), type)){
        return arr[type].pattern.replace(':slug?', '')+slug;
    }else{
        return defaultURL+slug;
    }
}

export function farmerURL(url){
    var base = window.urls['farmers'].replace(':slug?/:page?', '');
    if(isNull(url)){
        base = base.replace(/\/$/, "");
        url = "";
    }
    return base+url;
}

export function recipeURL(url){
    var base = window.urls['recipes'].replace(':slug?/:page?', '');
    if(isNull(url)){
        base = base.replace(/\/$/, "");
        url = "";
    }
    return base+url;
}

export function meatcutURL(url){
    var base = window.urls['meatcuts'].replace(':slug?', '');
    if(isNull(url) || isEmpty(url)){
        base = base.replace(/\/$/, "");
        url = "";
    }
    return base+url;
}
export function breedURL(url){
    var base = window.urls['breeds'].replace(':slug?/:page?', '');
    if(isNull(url)){
        base = base.replace(/\/$/, "");
        url = "";
    }
    return base+url;
}

export function farmshopProductURL(url){
    var base = window.urls['farmshop-products'].replace(':slug?', '');
    if(isNull(url) || isEmpty(url)){
        base = base.replace(/\/$/, "");
        url = "";
    }
    return base+url;
}

export function blogURL(url, categories){
    var base = window.urls['blogs'].replace(':category?/:slug?/:page?', '');

    if(isNil(url) && isNil(categories)){
        base = base.replace(/\/$/, "");
        url = "";
    }else{
        if (isNil(categories) || categories.length === 0) {
            var category = 'posts';
        }else{
            var category = first(categories)['slug'];
        }
        url = category+"/"+url;

    }
    return base+url;

}

export function getPath(num){
    var url = window.location.pathname.split('/').filter(str=>str.length>1);
    return url[num];
}

export function getPaymentLogos(){
    return siteInfo[currentLang()]['payments'];
}
export function animalPercent(value){
    var price = value;
    if(value >= 100)
        price = 100;
    if(value <= 3)
        price = 3;
    if(value < 100 && value > 97)
        price = 97;
    return price;
}

export function accentsTidy(s){
    var r=s.toLowerCase();
    r = r.replace(new RegExp(/\s/g),"");
    r = r.replace(new RegExp(/[àáâãäå]/g),"a");
    r = r.replace(new RegExp(/æ/g),"ae");
    r = r.replace(new RegExp(/ç/g),"c");
    r = r.replace(new RegExp(/[èéêë]/g),"e");
    r = r.replace(new RegExp(/[ìíîï]/g),"i");
    r = r.replace(new RegExp(/ñ/g),"n");
    r = r.replace(new RegExp(/[òóôõö]/g),"o");
    r = r.replace(new RegExp(/œ/g),"oe");
    r = r.replace(new RegExp(/[ùúûü]/g),"u");
    r = r.replace(new RegExp(/[ýÿ]/g),"y");
    r = r.replace(new RegExp(/\W/g),"");
    return r;
}

export function hasClass(el, className) {
    if (el.classList) return el.classList.contains(className);
    return !!el.className.match(new RegExp('(\\s|^)' + className + '(\\s|$)'));
}

export function addClass(el, className) {
   if (el.classList) el.classList.add(className);
   else if (!hasClass(el, className)) el.className += " " + className;
}

export function removeClass(el, className) {
    if (el.classList) el.classList.remove(className);
    else if (hasClass(el, className)) {
      var reg = new RegExp('(\\s|^)' + className + '(\\s|$)');
      el.className=el.className.replace(reg, ' ');
    }
}

export function setCookie(name, value, days, path="/") {
    var expires = "";
    if (days) {
        var date = new Date();
        date.setTime(date.getTime() + (days*24*60*60*1000));
        expires = "; expires=" + date.toUTCString();
    }
    document.cookie = name + "=" + (value || "")  + expires + "; path="+path;
}
export function getCookie(name) {
    var nameEQ = name + "=";
    var ca = document.cookie.split(';');
    for(var i=0;i < ca.length;i++) {
        var c = ca[i];
        while (c.charAt(0)==' ') c = c.substring(1,c.length);
        if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length,c.length);
    }
    return null;
}

// Usage: url = http://google.com/?test=1&ac=2
// getUrlVars()["test"]; or getUrlVars()["ac"];
export function getUrlVars() {
    var vars = {};
    window.location.href.replace(/[?&]+([^=&]+)=([^&]*)/gi, function(m,key,value) {
        vars[key] = value;
    });
    return vars;
}

export function listFilter(type){
    var data = flatstore.get(type+'_data');
    // We should remove this line after Akbar change the recipes API ('data' array)
    if(data && ['recipes', 'faq', 'breeds'].includes(type)){
        data = data.data;
    }

    var filterType = flatstore.get('list_filter_type');
    var page = flatstore.get('list_active_page');
    if(type == 'farmers' && filterType == 'general'){
        filterType = null;
    }
    var per_page = 18;
    if(type == 'faq'){
        per_page = 1000;
        if(isNil(filterType) || filterType.length < 2){
            filterType = 'general';
        }
    }
    var fdata = [];
    var j = 0; // nice variable name :)
    var i = (page-1)*per_page;
    var total = 0;
    var id = 0;
    if(data){
        data.map((item, index)=>{
            if(isNil(filterType) && type != 'blog'){
                if(j < per_page && id >= i){
                    fdata.push(item);
                    j++;
                }
                id++;
            }else{
                // Akbar should change animal_type to animal_types in FAQ API
                if(['faq', 'breeds'].includes(type)){
                    if(item.animal_type && item.animal_type.includes(filterType)){
                        total++;
                        if(j < per_page && id >= i){
                            fdata.push(item);
                            j++;
                        }
                        id++;
                    }
                }else if(type == 'blog'){
                    item.categories.map(cat=>{
                        if(isNil(filterType)){
                            total++;
                            if(j < per_page && id >= i){
                                if(j < 3){
                                    // Checking if two consecutive posts are not similar
                                    if (fdata.length > 0 && fdata[fdata.length - 1] === item) {
                                        return;
                                      }
                                    fdata.push(item);
                                    j++;
                                }
                            }
                            id++;
                        }else{
                            if(cat.slug == filterType){
                                total++;
                                if(j < per_page && id >= i){
                                    fdata.push(item);
                                    j++;
                                }
                                id++;
                            }
                        }

                    })

                }else{

                    if(item.animal_types && item.animal_types.includes(filterType) && item.slug != i18n.t("farmers.newfarmerslug")){
                        total++;
                        if(j < per_page && id >= i){
                            fdata.push(item);
                            j++;
                        }
                        id++;
                    }else{
                        if(item.slug == i18n.t("farmers.newfarmerslug")){
                            total++;
                        }
                    }

                }

            }

        })
        if(type == 'farmers' && filterType != 'game'){

            data.map((item, index)=>{
                if(!isNull(filterType) && Math.ceil(total/per_page) == page && item.slug == i18n.t("farmers.newfarmerslug")){
                    if(item.animal_types && item.animal_types.includes(filterType)){
                        fdata.push(item);
                        total++; // for new farmers
                    }
                }
            })
        }

        if(isNil(filterType)){
            fdata.total = data.length;
        }else{
            fdata.total = total;
        }

    }
    fdata.per_page = per_page;
    flatstore.set('list_filtered_data', fdata);
}
export function listSearch(type, search){
    var data = flatstore.get(type+'_data');
    // We should remove this line after Akbar change the recipes API ('data' array)
    if(data && ['recipes', 'faq', 'breeds'].includes(type)){
        data = data.data;
    }
    var fdata = [];
    if(search && search.length > 1){
        search = search.toLowerCase();
        var fdata = [];
        data.map((item, index)=>{
            if(type == 'faq'){
                if((item.question && accentsTidy(item.question).includes(search)) || (item.answer && accentsTidy(item.answer).includes(search))){
                    fdata.push(item);
                }
            }else if(type == 'blog'){
                if((item.title && accentsTidy(item.title).includes(search)) || (item.body && accentsTidy(item.body).includes(search))){
                    fdata.push(item);
                }
            }else{
                if((item.title && accentsTidy(item.title).includes(search)) || (item.location && accentsTidy(item.location).includes(search)) || (item.body && accentsTidy(item.body).includes(search))){
                    fdata.push(item);
                }
            }
        });
        if(fdata.length < 1){
            flatstore.set('list_search_status', 'noresult');
            flatstore.set('list_filtered_data', fdata);
        }else{
            fdata.total = 100;
            fdata.per_page = 1000;
            flatstore.set('list_filtered_data', fdata);
            flatstore.set('list_search_status', null);
        }
    }else{
        flatstore.set('list_search_status', null);
    }
}

export function checkRedirects(){
    var pathname = window.location.pathname;
    var redirects_url = 'https://www.grutto.com/configs/redirects.json';
    var redirects = getStorage('redirects');
    if(!redirects){
        axios.get(redirects_url).then(function(response){
            var items = response.data;
            if(items){
                // expire after one week
                setStorage('redirects', items, 604800);
                Object.keys(items).map((item, index)=>{
                    if(pathname == item){
                        window.location.replace(window.location.href.replace(pathname, Object.values(items)[index].to));

                    }
                });
            }
        }).catch(err => {
            console.log(err);
        });
    }else{
        // cached
        Object.keys(redirects).map((item, index)=>{
            if(pathname == item){
                window.location.replace(window.location.href.replace(pathname, Object.values(redirects)[index].to));
            }
        })
    }

}

export function addTimeAndGetFormattedDate(timeToAdd, unit) {
  const today = new Date();
  const locale = siteInfo[currentLang()]['lang'];
  let resultDate;

  if (unit === 'week') {
    resultDate = new Date(today.setDate(today.getDate() + (timeToAdd * 7)));
  } else if (unit === 'month') {
    resultDate = new Date(today.setMonth(today.getMonth() + timeToAdd));
  }

  // Using Intl.DateTimeFormat for formatting and internationalization
  const formatter = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const formattedDate = formatter.format(resultDate);

  return formattedDate; // e.g., "4 April 2024" or "4. April 2024" depending on locale
}

// check webp support:
export function webp_support() {
    if(navigator.vendor.match(/apple/i)){
        var webp_enable = getStorage('webp_enable');
        var webp_disable = getStorage('webp_disable');
        if(webp_enable || webp_disable){

        }else{
            var kTestImages = {
                lossy: "UklGRiIAAABXRUJQVlA4IBYAAAAwAQCdASoBAAEADsD+JaQAA3AAAAAA",
                lossless: "UklGRhoAAABXRUJQVlA4TA0AAAAvAAAAEAcQERGIiP4HAA==",
                alpha: "UklGRkoAAABXRUJQVlA4WAoAAAAQAAAAAAAAAAAAQUxQSAwAAAARBxAR/Q9ERP8DAABWUDggGAAAABQBAJ0BKgEAAQAAAP4AAA3AAP7mtQAAAA==",
                animation: "UklGRlIAAABXRUJQVlA4WAoAAAASAAAAAAAAAAAAQU5JTQYAAAD/////AABBTk1GJgAAAAAAAAAAAAAAAAAAAGQAAABWUDhMDQAAAC8AAAAQBxAREYiI/gcA"
            };
            var img = new Image();

            img.onload = function () {
                var result = (img.width > 0) && (img.height > 0);
                if(result){
                    setStorage('webp_enable', 1, 604800); // for a week
                    //return true;
                }
            };

            img.onerror = function () {
                setStorage('webp_disable', 1, 604800); // for a week
            };
            img.src = "data:image/webp;base64," + kTestImages['lossy'];
        }
    }
}

export function calculateTimeToRead(content){
    if(content){
        var wordsPerMinute = 200;
        var noOfWords = content.split(/\s/g).length;
        var minutes = noOfWords / wordsPerMinute;
        var readTime = Math.ceil(minutes);
        return readTime;
    }
}

// deadline: "HH:MM" (e.g., "09:30") or number hour (e.g., 9)
export function dynamic_delivery_promise(deadline) {
  const MSG = {
    weekend: "farmshop.top_subtitle_1_weekend",
    next_day: "farmshop.top_subtitle_1_next_day",
    two_days: "farmshop.top_subtitle_1_two_days",
  };

  const RULES = {
    6: { before: "weekend", after: "weekend" },   // Sat
    0: { before: "two_days", after: "two_days" }, // Sun
    1: { before: "next_day", after: "two_days" }, // Mon
    2: { before: "next_day", after: "two_days" }, // Tue
    3: { before: "next_day", after: "two_days" }, // Wed
    4: { before: "next_day", after: "two_days" }, // Thu
    5: { before: "next_day", after: "weekend" },  // Fri
  };

  const toMinutes = (v) => {
    if (v == null) return null;
    if (typeof v === "number") return v * 60;
    if (typeof v === "string") {
      const m = v.match(/^(\d{1,2})(?::(\d{1,2}))?$/);
      if (!m) return null;
      const h = parseInt(m[1], 10);
      const min = parseInt(m[2] ?? "0", 10);
      return h * 60 + min;
    }
    return null;
  };

  try {
    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    const cutoff = toMinutes(deadline);

    if (cutoff == null) return null;

    const time = nowMin < cutoff ? "before" : "after";
    const rule = RULES[now.getDay()];
    return rule ? MSG[rule[time]] ?? null : null;
  } catch {
    return null;
  }
}


export function objectToQueryString(obj) {
  const keys = Object.keys(obj);
  const keyValuePairs = keys.map(key => {
    return encodeURIComponent(key) + '=' + encodeURIComponent(obj[key]);
  });
  return keyValuePairs.join('&');
}

/**
 * Effective quantity for subscription display/stock: subscription_amount applies only to non-gift qty.
 * Formula: (totalQty - giftQty) * subscriptionAmount + giftQty
 * @param {number} totalQty
 * @param {number} giftQty
 * @param {number} subscriptionAmount
 * @returns {number}
 */
export function subscriptionQuantityWithGift(totalQty, giftQty, subscriptionAmount) {
  const total = Math.max(0, parseInt(totalQty, 10) || 0);
  const gift = Math.max(0, Math.min(parseInt(giftQty, 10) || 0, total));
  const sub = Math.max(1, parseInt(subscriptionAmount, 10) || 1);
  const regular = total - gift;
  return regular * sub + gift;
}